/**
 * Product configuration tokens.
 *
 * The working name "Gigify" is NOT cleared for public launch (an existing live-music product
 * uses it). Per the master spec (§1, §44) and AGENTS.md, the public product name must be
 * configuration, replaceable without a code search-and-rewrite. Nothing outside this module
 * should hard-code the public name, public domain, or sender identities.
 */

export interface ProductConfig {
  /** Public display name shown in UI, metadata, and copy. */
  readonly name: string;
  /** Short name used where space is constrained. */
  readonly shortName: string;
  /** Public domain serving audience + API on one origin (§14.1). */
  readonly publicDomain: string;
  /** Base HTTPS URL for public audience pages and QR codes. */
  readonly publicBaseUrl: string;
  /** Support / from email identity (never hard-code elsewhere). */
  readonly supportEmail: string;
  /** Whether trademark/naming clearance has completed. Gates public launch only. */
  readonly nameCleared: boolean;
}

function env(key: string): string | undefined {
  // Guard for non-Node runtimes (browser bundles inline these at build time).
  const proc = (globalThis as { process?: { env?: Record<string, string | undefined> } }).process;
  return proc?.env?.[key];
}

const DEFAULT_DOMAIN = 'gigify.digitaldevops.io';

/**
 * Resolve product configuration from environment with safe working-name defaults.
 * Override via PRODUCT_NAME, PRODUCT_SHORT_NAME, PUBLIC_DOMAIN, SUPPORT_EMAIL.
 */
export function resolveProductConfig(): ProductConfig {
  const name = env('PRODUCT_NAME') ?? 'Gigify';
  const shortName = env('PRODUCT_SHORT_NAME') ?? name;
  const publicDomain = env('PUBLIC_DOMAIN') ?? DEFAULT_DOMAIN;
  const publicBaseUrl = env('PUBLIC_BASE_URL') ?? `https://${publicDomain}`;
  const supportEmail = env('SUPPORT_EMAIL') ?? `support@${publicDomain}`;
  const nameCleared = env('PRODUCT_NAME_CLEARED') === 'true';
  return { name, shortName, publicDomain, publicBaseUrl, supportEmail, nameCleared };
}

/** Convenience singleton resolved from the current environment. */
export const productConfig: ProductConfig = resolveProductConfig();

/** Build the canonical public event URL for a gig public code (§14.2). */
export function publicEventUrl(
  publicCode: string,
  sourceCode?: string,
  config: ProductConfig = productConfig,
): string {
  const url = new URL(`/g/${encodeURIComponent(publicCode)}`, config.publicBaseUrl);
  if (sourceCode) url.searchParams.set('src', sourceCode);
  return url.toString();
}

/** Build the canonical stable artist page URL (§14.3). */
export function stableArtistUrl(
  artistSlug: string,
  config: ProductConfig = productConfig,
): string {
  return new URL(`/a/${encodeURIComponent(artistSlug)}`, config.publicBaseUrl).toString();
}
